// src/components/hooks/useTrabajadores.ts

import { useState, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

// Formatea 'YYYY-MM-DD' como fecha local (evita el desfase de un día que
// causa new Date() al interpretar la cadena como UTC — "31 dic" → "1 ene").
const fechaLocal = (fecha: string) => {
  const [y, m, d] = fecha.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-ES');
};

/* ============================================
   TIPOS
============================================= */
export interface AltaTrabajadorParams {
  idEmpresa: string;
  idUsuario: string;
  idPerfil: string; // id_perfil de 'trabajador' en la tabla perfiles
}

export interface ResumenTrabajadores {
  id_empresa: string;
  trabajadores_activos: number;
  en_periodo_prueba: number;
  facturando: number;
  con_pago_pendiente: number;
}

export interface TrabajadorLista {
  id_empresa_trabajador: string | null; // NULL = todavía no se ha habilitado
  id_empresa: string;
  id_usuario: string;
  nombre: string | null;
  email: string;
  fecha_alta: string | null;
  es_incluido_plan: boolean | null;
  motivo_gratis: 'plan' | null; // solo cupo del plan — la promo ya no se graba por trabajador
  stripe_status: string | null;
  cancelacion_programada: boolean;
  fecha_fin_acceso: string | null;
  cubierto_por_promo: boolean; // promo activa de la empresa, calculada en tiempo real
  tiene_acceso: boolean;
}

/* ============================================
   ESTADO DEL PLAN PARA PODER HABILITAR
   Una sola comprobación reutilizable: sin plan activo (o con el plan ya
   cancelado en Stripe) NO se puede habilitar ni reactivar a nadie —
   mismo criterio que cuando el plan no vende trabajadores adicionales.
   OJO: antes se escribía `if (planActivo && ...)`, con lo que el caso
   "no hay plan" se colaba entero y dejaba trabajadores renovándose sin
   plan del que colgar.
============================================= */
type EstadoPlanEmpresa =
  | { puede: true }
  | { puede: false; motivo: string; planNoPermite?: boolean };

async function comprobarPlanParaHabilitar(
  idEmpresa: string
): Promise<EstadoPlanEmpresa> {
  const { data: planActivo, error } = await supabase
    .from('planes_contratados')
    .select('id_plan, auto_renovacion, id_plan_pendiente, stripe_status')
    .eq('id_empresa', idEmpresa)
    .eq('activo', true)
    .maybeSingle();

  if (error) {
    Sentry.captureException(error, { extra: { idEmpresa } });
    return { puede: false, motivo: 'No se pudo comprobar el estado de tu plan.' };
  }

  if (!planActivo || planActivo.stripe_status === 'canceled') {
    return {
      puede: false,
      motivo: 'No tienes ningún plan activo. Contrata uno para poder habilitar trabajadores.',
      planNoPermite: true,
    };
  }

  if (planActivo.auto_renovacion === false || planActivo.id_plan_pendiente) {
    return {
      puede: false,
      motivo: 'El plan tiene un cambio programado — no se puede habilitar a nadie hasta que se resuelva.',
    };
  }

  return { puede: true };
}

/* ============================================
   CUPO Y PRECIO PARA TRABAJADORES
   Se usa el plan CONTRATADO, no el efectivo. Aunque una promoción
   conceda temporalmente un plan superior (lanzamiento → Premium), no se
   permite dar de alta trabajadores que el plan contratado no vende: al
   terminar la promoción se quedarían sin cobertura y habría que
   cancelarlos todos de golpe.
   (Las FUNCIONES sí van por plan efectivo — eso se resuelve en la vista
   y en la interfaz, no aquí.)
============================================= */
async function cargarLimitesTrabajadores(idEmpresa: string): Promise<{
  trabajadoresIncluidos: number;
  precioTrabajadorAdicional: number;
}> {
  const { data, error } = await supabase
    .from('vista_plan_efectivo_empresa')
    .select('trabajadores_incluidos_contratado, precio_trabajador_adicional_contratado')
    .eq('id_empresa', idEmpresa)
    .maybeSingle();

  if (error) {
    Sentry.captureException(error, { extra: { idEmpresa } });
    console.error('Error al cargar los límites de trabajadores:', error);
  }

  return {
    trabajadoresIncluidos: data?.trabajadores_incluidos_contratado ?? 0,
    precioTrabajadorAdicional: data?.precio_trabajador_adicional_contratado ?? 0,
  };
}

/* ============================================
   RESUMEN DE TRABAJADORES (solo lectura)
   Usado en Suscripción: activos / en trial / facturando.
============================================= */
export async function cargarResumenTrabajadores(
  idEmpresa: string
): Promise<ResumenTrabajadores> {
  const { data, error } = await supabase
    .from('vista_empresa_trabajadores_resumen')
    .select('*')
    .eq('id_empresa', idEmpresa)
    .maybeSingle();

  if (error) {
    Sentry.captureException(error, { extra: { idEmpresa } });
    console.error('Error al cargar el resumen de trabajadores:', error);
  }

  return (
    data ?? {
      id_empresa: idEmpresa,
      trabajadores_activos: 0,
      en_periodo_prueba: 0,
      facturando: 0,
      con_pago_pendiente: 0,
    }
  );
}

/* ============================================
   LISTADO DE TRABAJADORES CON ESTADO DE ACCESO
   Usado en Suscripción para el switch de habilitar/deshabilitar.
============================================= */
export async function cargarListaTrabajadores(
  idEmpresa: string
): Promise<TrabajadorLista[]> {
  const { data, error } = await supabase
    .from('vista_trabajadores_con_acceso')
    .select('*')
    .eq('id_empresa', idEmpresa);

  if (error) {
    Sentry.captureException(error, { extra: { idEmpresa } });
    console.error('Error al cargar el listado de trabajadores:', error);
    return [];
  }

  return data as TrabajadorLista[];
}

/* ============================================
   ALTA DE TRABAJADOR
   1) Inserta la fila en usuarios_perfiles (perfil trabajador)
   2) Inserta la fila en empresa_trabajadores (fecha_alta = hoy)
   3) Llama a la Edge Function que crea la suscripción de Stripe y guarda
      el id_suscripcion_stripe devuelto en empresa_trabajadores
============================================= */
export function useTrabajadores() {
  const [dandoAlta, setDandoAlta] = useState(false);
  const [dandoBaja, setDandoBaja] = useState(false);
  const [cambiandoAcceso, setCambiandoAcceso] = useState<string | null>(null);

  // Set síncrono (no state) de trabajadores con una operación en curso.
  // Se comprueba ANTES de tocar el estado de React, para que un doble
  // clic muy rápido no se cuele en la ventana de tiempo entre el primer
  // clic y el repintado (el estado de React no bloquea nada hasta que se
  // re-renderiza, esto sí es inmediato).
  const enCurso = useRef<Set<string>>(new Set());

  const altaTrabajador = useCallback(
    async ({ idEmpresa, idUsuario, idPerfil }: AltaTrabajadorParams) => {
      setDandoAlta(true);
      try {
        // 0) Sin plan activo (o con baja/cambio programado) no se da de
        //    alta a nadie: quedaría colgando de un plan inexistente.
        const estadoPlan = await comprobarPlanParaHabilitar(idEmpresa);
        if (!estadoPlan.puede) {
          toast.error(estadoPlan.motivo);
          return {
            ok: false,
            stripeOk: false,
            incluidoEnPlan: false,
            planNoPermite: estadoPlan.planNoPermite ?? false,
          };
        }

        // 1) Cupo y precio según el plan CONTRATADO. Se comprueba ANTES
        //    de crear nada, para no dejar un perfil de trabajador sin poder
        //    tener acceso nunca si el plan no lo permite.
        const { trabajadoresIncluidos, precioTrabajadorAdicional } =
          await cargarLimitesTrabajadores(idEmpresa);

        const { count: incluidosUsados } = await supabase
          .from('empresa_trabajadores')
          .select('*', { count: 'exact', head: true })
          .eq('id_empresa', idEmpresa)
          .eq('activo', true)
          .eq('es_incluido_plan', true);

        const entraEnCupo = (incluidosUsados ?? 0) < trabajadoresIncluidos;

        if (!entraEnCupo && precioTrabajadorAdicional <= 0) {
          toast.error(
            'Tu plan contratado no incluye trabajadores adicionales. Cambia de plan para poder añadirlos.'
          );
          return { ok: false, stripeOk: false, incluidoEnPlan: false, planNoPermite: true };
        }

        // 2) Alta del perfil (rol trabajador en esta empresa)
        const { error: errorPerfil } = await supabase
          .from('usuarios_perfiles')
          .insert({
            id_usuario: idUsuario,
            id_empresa: idEmpresa,
            id_perfil: idPerfil,
            activo: true,
          });

        if (errorPerfil) throw errorPerfil;

        // 3) Alta en empresa_trabajadores (fecha_alta por defecto = hoy)
        const { data: trabajador, error: errorTrabajador } = await supabase
          .from('empresa_trabajadores')
          .insert({
            id_empresa: idEmpresa,
            id_usuario: idUsuario,
            es_incluido_plan: entraEnCupo,
            motivo_gratis: entraEnCupo ? 'plan' : null,
          })
          .select()
          .single();

        if (errorTrabajador) throw errorTrabajador;

        // Si entra en el cupo gratis del plan, no hace falta suscripción
        // de Stripe — se queda aquí con acceso ya garantizado.
        if (entraEnCupo) {
          toast.success('Trabajador dado de alta (incluido en tu plan)');
          return { ok: true, stripeOk: true, incluidoEnPlan: true };
        }

        // 4) Fuera de cupo: crear la suscripción de Stripe
        const { data: stripeData, error: errorStripe } =
          await supabase.functions.invoke('crear-suscripcion-trabajador', {
            body: {
              id_empresa: idEmpresa,
              id_usuario: idUsuario,
              id_empresa_trabajador: trabajador.id_empresa_trabajador,
            },
          });

        if (errorStripe) {
          // El trabajador ya quedó dado de alta; el fallo de Stripe no debe
          // bloquear el flujo, pero sí hay que avisar para revisarlo.
          Sentry.captureException(errorStripe, {
            extra: { idEmpresa, idUsuario },
          });
          toast.warning(
            'Trabajador dado de alta, pero hubo un problema al crear su suscripción. Se revisará automáticamente.'
          );
          return { ok: true, stripeOk: false, incluidoEnPlan: false };
        }

        // 5) Guardar el id_suscripcion_stripe devuelto (la Edge Function
        //    ya lo hace ella misma, esto es solo redundancia de seguridad)
        if (stripeData?.id_suscripcion_stripe) {
          await supabase
            .from('empresa_trabajadores')
            .update({
              id_suscripcion_stripe: stripeData.id_suscripcion_stripe,
              stripe_status: stripeData.stripe_status ?? null,
            })
            .eq('id_empresa_trabajador', trabajador.id_empresa_trabajador);
        }

        toast.success('Trabajador dado de alta.');
        return { ok: true, stripeOk: true, incluidoEnPlan: false };
      } catch (error) {
        Sentry.captureException(error, { extra: { idEmpresa, idUsuario } });
        toast.error('Error al dar de alta al trabajador');
        return { ok: false, stripeOk: false, incluidoEnPlan: false };
      } finally {
        setDandoAlta(false);
      }
    },
    []
  );

  /* ============================================
     BAJA DE TRABAJADOR
     1) Desactiva la fila en usuarios_perfiles
     2) Desactiva la fila en empresa_trabajadores (el trigger de historial
        registra la BAJA automáticamente)
     3) Llama a la Edge Function que cancela la suscripción en Stripe
  ============================================= */
  const bajaTrabajador = useCallback(
    async (idEmpresa: string, idUsuario: string) => {
      setDandoBaja(true);
      try {
        const { error: errorPerfil } = await supabase
          .from('usuarios_perfiles')
          .update({ activo: false })
          .eq('id_empresa', idEmpresa)
          .eq('id_usuario', idUsuario);

        if (errorPerfil) throw errorPerfil;

        const { data: trabajador, error: errorTrabajador } = await supabase
          .from('empresa_trabajadores')
          .update({ activo: false, fecha_baja: new Date().toISOString() })
          .eq('id_empresa', idEmpresa)
          .eq('id_usuario', idUsuario)
          .select()
          .single();

        if (errorTrabajador) throw errorTrabajador;

        if (trabajador?.id_suscripcion_stripe) {
          const { error: errorStripe } = await supabase.functions.invoke(
            'cancelar-suscripcion-trabajador',
            { body: { id_suscripcion_stripe: trabajador.id_suscripcion_stripe } }
          );

          if (errorStripe) {
            Sentry.captureException(errorStripe, { extra: { idEmpresa, idUsuario } });
            toast.warning(
              'Trabajador dado de baja, pero hubo un problema al cancelar su suscripción. Se revisará automáticamente.'
            );
            return { ok: true, stripeOk: false };
          }
        }

        toast.success('Trabajador dado de baja');
        return { ok: true, stripeOk: true };
      } catch (error) {
        Sentry.captureException(error, { extra: { idEmpresa, idUsuario } });
        toast.error('Error al dar de baja al trabajador');
        return { ok: false, stripeOk: false };
      } finally {
        setDandoBaja(false);
      }
    },
    []
  );

  /* ============================================
     HABILITAR ACCESO
     Si el trabajador aún no tiene fila en empresa_trabajadores (nunca se
     habilitó), la crea ahora: si hay hueco en el cupo incluido del plan
     efectivo, queda con acceso inmediato; si no, crea la suscripción de
     Stripe. Si ya tenía fila pero con baja programada, la deshace.
  ============================================= */
  const habilitarAcceso = useCallback(
    async (trabajador: TrabajadorLista) => {
      // Bloqueo síncrono: si ya hay una operación en curso para este
      // trabajador (un clic anterior todavía no ha terminado), se ignora
      // este nuevo intento sin más — evita cobros dobles por doble clic.
      if (enCurso.current.has(trabajador.id_usuario)) {
        return { ok: false };
      }
      enCurso.current.add(trabajador.id_usuario);

      setCambiandoAcceso(trabajador.id_usuario);
      try {
        // Sin plan activo, o con el plan de baja / con una bajada
        // programada, no se puede habilitar a NADIE en modo alta — ni
        // siquiera "deshacer" la baja individual de uno concreto, porque
        // esa baja puede venir precisamente del arrastre del propio plan
        // (ver cancelar-plan). Solo al reactivar el plan entero se
        // destraban todos a la vez.
        // OJO: una BAJADA de plan (Subscription Schedule) deja
        // auto_renovacion en true — es una suscripción normal, solo con
        // una fase futura distinta — así que se comprueba también
        // id_plan_pendiente, no solo auto_renovacion. Y el caso "no hay
        // plan" cuenta como bloqueo, no como vía libre.
        const estadoPlan = await comprobarPlanParaHabilitar(trabajador.id_empresa);
        if (!estadoPlan.puede) {
          toast.error(estadoPlan.motivo);
          return { ok: false, planNoPermite: estadoPlan.planNoPermite ?? false };
        }

        let idEmpresaTrabajador = trabajador.id_empresa_trabajador;
        let esIncluidoPlanExistente = trabajador.es_incluido_plan;

        let filaInactivaId: string | null = null;

        // 0) El estado que trae `trabajador` viene del último listado
        //    cargado en el frontend, que puede estar desactualizado (por
        //    ejemplo si un intento anterior creó la fila pero falló más
        //    adelante, antes de refrescar la pantalla). Comprobamos
        //    siempre contra la BD para no intentar un INSERT duplicado.
        //    OJO: se busca CUALQUIER fila (activa o no) — la clave única
        //    (id_empresa, id_usuario) no distingue por activo, así que un
        //    trabajador dado de baja antes sigue "ocupando" esa fila.
        if (!idEmpresaTrabajador) {
          const { data: filaExistente } = await supabase
            .from('empresa_trabajadores')
            .select('id_empresa_trabajador, activo, es_incluido_plan, stripe_status')
            .eq('id_empresa', trabajador.id_empresa)
            .eq('id_usuario', trabajador.id_usuario)
            .maybeSingle();

          if (filaExistente?.activo) {
            idEmpresaTrabajador = filaExistente.id_empresa_trabajador;
            esIncluidoPlanExistente = filaExistente.es_incluido_plan;
          } else if (filaExistente) {
            filaInactivaId = filaExistente.id_empresa_trabajador;
          }
        }

        // 1) Si sigue sin existir fila, crearla comprobando el cupo del
        //    plan CONTRATADO. Durante la promoción el trabajador será
        //    gratis (trial), pero solo se permite darlo de alta si el plan
        //    contratado lo cubrirá cuando la promo termine.
        if (!idEmpresaTrabajador) {
          const { trabajadoresIncluidos, precioTrabajadorAdicional } =
            await cargarLimitesTrabajadores(trabajador.id_empresa);

          const { count: incluidosUsados } = await supabase
            .from('empresa_trabajadores')
            .select('*', { count: 'exact', head: true })
            .eq('id_empresa', trabajador.id_empresa)
            .eq('activo', true)
            .eq('es_incluido_plan', true);

          const entraEnCupo = (incluidosUsados ?? 0) < trabajadoresIncluidos;

          // Fuera de cupo: el plan CONTRATADO tiene que vender
          // trabajadores adicionales (precio_trabajador_adicional > 0). Si
          // no, bloquear sin crear ni la fila ni la suscripción.
          if (!entraEnCupo && precioTrabajadorAdicional <= 0) {
            toast.error(
              'Tu plan contratado no incluye trabajadores adicionales. Cambia de plan para poder añadirlos.'
            );
            return { ok: false, planNoPermite: true };
          }

          const { data: nuevaFila, error: errorInsert } = await supabase
            .from('empresa_trabajadores')
            .upsert(
              {
                ...(filaInactivaId ? { id_empresa_trabajador: filaInactivaId } : {}),
                id_empresa: trabajador.id_empresa,
                id_usuario: trabajador.id_usuario,
                es_incluido_plan: entraEnCupo,
                motivo_gratis: entraEnCupo ? 'plan' : null,
                activo: true,
                fecha_baja: null,
                cancelacion_programada: false,
              },
              { onConflict: 'id_empresa,id_usuario' }
            )
            .select()
            .single();

          if (errorInsert) throw errorInsert;
          idEmpresaTrabajador = nuevaFila.id_empresa_trabajador;

          if (entraEnCupo) {
            toast.success(
              `Acceso habilitado para ${trabajador.nombre ?? trabajador.email} (incluido en tu plan)`
            );
            return { ok: true, generoFactura: false };
          }
        } else if (esIncluidoPlanExistente) {
          // Si tenía una baja programada (todavía ocupa la plaza, la
          // empresa no la ha perdido), simplemente deshacerla.
          if (trabajador.cancelacion_programada) {
            const { error: errorDeshacer } = await supabase
              .from('empresa_trabajadores')
              .update({ cancelacion_programada: false, fecha_fin_acceso: null })
              .eq('id_empresa_trabajador', idEmpresaTrabajador);

            if (errorDeshacer) throw errorDeshacer;

            toast.success(
              `Baja cancelada para ${trabajador.nombre ?? trabajador.email} — sigue incluido en el plan`
            );
          }
          return { ok: true, generoFactura: false };
        }

        // 2) Si había una baja programada (cancel_at_period_end), deshacerla
        //    en vez de crear una suscripción nueva — ya existe una activa.
        //    La Edge Function vuelve a validar el estado del plan por su
        //    cuenta: este guard es solo la primera línea.
        if (trabajador.cancelacion_programada) {
          const { error: errorReactivar } = await supabase.functions.invoke(
            'reactivar-suscripcion-trabajador',
            { body: { id_empresa_trabajador: idEmpresaTrabajador } }
          );
          if (errorReactivar) throw errorReactivar;

          toast.success(
            `Baja cancelada para ${trabajador.nombre ?? trabajador.email} — sigue facturando con normalidad`
          );
          return { ok: true, generoFactura: false }; // sigue la misma suscripción, no genera una factura nueva ahora
        }

        // 3) Fuera de cupo y sin baja programada: crear la suscripción de Stripe
        const { data: stripeData, error: errorStripe } =
          await supabase.functions.invoke('crear-suscripcion-trabajador', {
            body: {
              id_empresa: trabajador.id_empresa,
              id_usuario: trabajador.id_usuario,
              id_empresa_trabajador: idEmpresaTrabajador,
            },
          });

        if (errorStripe) throw errorStripe;

        // La propia Edge Function ya actualizó id_suscripcion_stripe y
        // stripe_status en empresa_trabajadores. Si la empresa está en
        // promoción, la suscripción arranca con trial hasta el fin de la
        // promo y no se cobra nada todavía.
        const enTrial = stripeData?.stripe_status === 'trialing';

        toast.success(
          enTrial
            ? `Acceso habilitado para ${trabajador.nombre ?? trabajador.email} (gratis mientras dure la promoción)`
            : `Acceso habilitado para ${trabajador.nombre ?? trabajador.email} (se factura desde hoy)`
        );
        return { ok: true, generoFactura: !enTrial }; // solo genera cobro/factura real si NO está en trial
      } catch (error) {
        Sentry.captureException(error, {
          extra: { id_usuario: trabajador.id_usuario, id_empresa: trabajador.id_empresa },
        });
        toast.error('No se pudo habilitar el acceso');
        return { ok: false };
      } finally {
        enCurso.current.delete(trabajador.id_usuario);
        setCambiandoAcceso(null);
      }
    },
    []
  );

  /* ============================================
     DESHABILITAR ACCESO
     Programa el cierre para el final del periodo ya pagado (el trabajador
     conserva el acceso hasta esa fecha), tanto si estaba en el cupo del
     plan como si se facturaba aparte por Stripe.
  ============================================= */
  const deshabilitarAcceso = useCallback(
    async (trabajador: TrabajadorLista) => {
      if (!trabajador.id_empresa_trabajador) return { ok: true }; // nunca se habilitó, nada que hacer

      if (enCurso.current.has(trabajador.id_usuario)) {
        return { ok: false };
      }
      enCurso.current.add(trabajador.id_usuario);

      setCambiandoAcceso(trabajador.id_usuario);
      try {
        // Trabajador incluido en el cupo del plan: nunca pasó por Stripe,
        // pero la empresa ya pagó el periodo actual (que incluye esa
        // plaza) — así que conserva el acceso hasta que termine. No se
        // libera la plaza todavía (sigue contando para el cupo hasta
        // entonces).
        if (trabajador.es_incluido_plan) {
          // Fecha de corte: si hay promo activa que cubre a la empresa,
          // el acceso se mantiene hasta el fin de promo (igual que el
          // resto de trabajadores). Sin promo, hasta el fin de periodo
          // del plan ya pagado — pero SOLO si el plan de verdad tiene una
          // baja o cambio en curso; si está activo y renovándose sin nada
          // pendiente, su fecha_fin es solo el ciclo mensual (o dato
          // colgado) y no debe cortar el acceso.
          const { data: promoActiva } = await supabase
            .from('vista_empresa_promocion_activa')
            .select('fecha_fin')
            .eq('id_empresa', trabajador.id_empresa)
            .maybeSingle();

          let fechaFinAcceso: string | null = promoActiva?.fecha_fin ?? null;

          if (!fechaFinAcceso) {
            const { data: planActivo } = await supabase
              .from('planes_contratados')
              .select('fecha_fin, auto_renovacion, id_plan_pendiente')
              .eq('id_empresa', trabajador.id_empresa)
              .eq('activo', true)
              .maybeSingle();

            const planCambia =
              planActivo?.auto_renovacion === false || !!planActivo?.id_plan_pendiente;

            fechaFinAcceso = planCambia ? planActivo?.fecha_fin ?? null : null;
          }

          const { error: errorProgramar } = await supabase
            .from('empresa_trabajadores')
            .update({
              cancelacion_programada: true,
              fecha_fin_acceso: fechaFinAcceso,
            })
            .eq('id_empresa_trabajador', trabajador.id_empresa_trabajador);

          if (errorProgramar) throw errorProgramar;

          toast.success(
            fechaFinAcceso
              ? `Baja programada para ${trabajador.nombre ?? trabajador.email} — mantiene acceso hasta el ${fechaLocal(fechaFinAcceso)}`
              : `Baja programada para ${trabajador.nombre ?? trabajador.email}`
          );
          return { ok: true };
        }

        // La Edge Function programa la baja para el final del periodo ya
        // pagado (no cancela al instante) y actualiza empresa_trabajadores
        // ella misma — no hay que tocar la BD desde aquí.
        const { data: resultado, error: errorStripe } = await supabase.functions.invoke(
          'cancelar-suscripcion-trabajador',
          {
            body: {
              id_empresa_trabajador: trabajador.id_empresa_trabajador,
            },
          }
        );
        if (errorStripe) throw errorStripe;

        const fechaFinAcceso = resultado?.fechaFinAcceso as string | null | undefined;

        toast.success(
          fechaFinAcceso
            ? `Baja programada para ${trabajador.nombre ?? trabajador.email} — mantiene acceso hasta el ${fechaLocal(fechaFinAcceso)}`
            : `Acceso retirado para ${trabajador.nombre ?? trabajador.email}`
        );
        return { ok: true };
      } catch (error) {
        Sentry.captureException(error, {
          extra: { id_empresa_trabajador: trabajador.id_empresa_trabajador },
        });
        toast.error('No se pudo deshabilitar el acceso');
        return { ok: false };
      } finally {
        enCurso.current.delete(trabajador.id_usuario);
        setCambiandoAcceso(null);
      }
    },
    []
  );

  return {
    altaTrabajador,
    bajaTrabajador,
    habilitarAcceso,
    deshabilitarAcceso,
    dandoAlta,
    dandoBaja,
    cambiandoAcceso,
  };
}